import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ChartNoAxesCombined, Ellipsis } from 'lucide-react';
import { useMap } from 'react-leaflet';
import { getCrimeDisplayName } from '../../constants/crimes';
import { getStateAbbreviation } from '../../constants/locations';
import './MapMinMax.css';

const MapMinMax = ({ 
  selectedLocation, 
  activeTab,
  locationTypeData,
  onMinMaxChange,
  selectedCrime,
  currentRange,
  popupOpen,
  onMenuStateChange,
  selectedSource,
  getActiveCompareMode,
  rateViewMode,
  onCompareModeChange,
  onUnifiedCompareModeChange
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const [compareMenuOpen, setCompareMenuOpen] = useState(false);
  const [compareButtonRef, setCompareButtonRef] = useState(null);
  const compareMenuRef = useRef(null);

  const map = useMap();
  const isNation = selectedLocation?.info?.type === 'nation';
  const isCounty = selectedLocation?.info?.type === 'county';
  const isCity = selectedLocation?.info?.type === 'city';
  const isMetro = selectedLocation?.info?.type === 'metro';
  const isCountyOrCity = isCounty || isCity;
  const isOneCountyOrCity = locationTypeData.data.locations[0].total_counties_state === 1 || locationTypeData.data.locations[0].total_cities_state === 1
  const compareMode = getActiveCompareMode();

  const stateAbbr = React.useMemo(() => {
    const locationName = selectedLocation?.info?.name;
    
    if (!locationName) return '';
    
    if (locationName.startsWith('All ') && (locationName.includes('Counties') || locationName.includes('Cities'))) {
      const match = locationName.match(/All ([A-Z]{2})/);
      if (match) return match[1];
      return 'US';
    }
    
    if (locationName.includes(',')) {
      const parts = locationName.split(',');
      const statePart = parts[parts.length - 1].trim();
      return getStateAbbreviation(statePart) || statePart;
    }
    
    return selectedLocation?.info?.stateAbbr || 
           getStateAbbreviation(selectedLocation?.info?.state) || 
           '';
  }, [selectedLocation?.info?.name, selectedLocation?.info?.stateAbbr, selectedLocation?.info?.state]);

  useEffect(() => {
    if (compareButtonRef) {
      compareButtonRef.style.backgroundColor = compareMenuOpen 
        ? 'rgba(26, 44, 78, 0.1)' 
        : 'transparent';
    }
  }, [compareMenuOpen, compareButtonRef]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.map-minmax-menu') && 
          !event.target.closest('.map-minmax-button')) {
        setShowMenu(false);
      }

      if (compareMenuOpen && 
          !event.target.closest('.map-ellipsis-button') && 
          !event.target.closest('.map-compare-menu')) {
        setCompareMenuOpen(false);
        setCompareButtonRef(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside, true);
    return () => document.removeEventListener('mousedown', handleClickOutside, true);
  }, [compareMenuOpen]);

  useEffect(() => {
    if (popupOpen) {
      setShowMenu(false);
    }
  }, [popupOpen]);
  
  useEffect(() => {
    const isAnyMenuOpen = showMenu || compareMenuOpen;
    if (onMenuStateChange) {
      onMenuStateChange(isAnyMenuOpen);
    }
  }, [showMenu, compareMenuOpen, onMenuStateChange]);

  const getRangeOptions = useCallback(() => {
    if (!locationTypeData?.data?.locations) return [];
    
    const crimeName = getCrimeDisplayName(selectedCrime, selectedSource);
    const locationType = selectedLocation.info?.type?.charAt(0).toUpperCase() + selectedLocation.info?.type?.slice(1);
  
    let comparisonPrefix = '';
    if (isCountyOrCity) {
      comparisonPrefix = compareMode === 'state' ? 
        (stateAbbr || '') : 'US';
    }
  
    switch (activeTab) {
      case 'reports':
      case 'totals':
        return [
          { value: 'count', label: `Total ${crimeName}`, isDefault: true },
          { value: 'count_avg', label: `${comparisonPrefix ? `${comparisonPrefix} ` : ''}${locationType} Average` },
          { value: 'avg_diff', label: `${comparisonPrefix ? `${comparisonPrefix} ` : ''}${locationType} Avg Difference` },
          { value: 'avg_diff_pct', label: `% ${comparisonPrefix ? `${comparisonPrefix} ` : ''}${locationType} Avg Difference` },
          { value: 'count_vs_avg', label: `Total ${crimeName} vs ${comparisonPrefix ? `${comparisonPrefix} ` : ''}${locationType} Average` },
          { value: 'rank', label: `${comparisonPrefix ? `${comparisonPrefix} ` : ''}${locationType} Rank` },
          { value: 'percentile', label: `${comparisonPrefix ? `${comparisonPrefix} ` : ''}${locationType} Percentile` }
        ];
      case 'trends':
        return [
          { value: 'count', label: `Total ${crimeName}` },
          { value: 'prev_count', label: 'Previous Total' },
          { value: 'trend_diff', label: 'Change from Previous Total' },
          { value: 'trend_diff_pct', label: '% Change from Previous Total', isDefault: true },
          { value: 'count_vs_prev', label: `Total ${crimeName} vs Previous Total` },
          ...(isNation ? [] : [
            { value: 'rank', label: `${comparisonPrefix ? `${comparisonPrefix} ` : ''}${locationType} Rank` },
            { value: 'percentile', label: `${comparisonPrefix ? `${comparisonPrefix} ` : ''}${locationType} Percentile` }
          ])
        ];
      case 'rates': {
        const getComparisonLabel = () => {
          if (isMetro) {
            return rateViewMode.rateType === 'metro' ? 'Metro' : 'National';
          }
          if (isCountyOrCity) {
            if (rateViewMode.rateType === (isCounty ? 'county' : 'city')) {
              return rateViewMode.rateScope === 'state' ? 
                `${stateAbbr} ${locationType}` : `US ${locationType}`;
            } else {
              return rateViewMode.rateScope === 'state' ? 
                `${stateAbbr} State` : 'US National';
            }
          }
          return 'National';
        };
        
        const compLabel = getComparisonLabel();
        
        return [
          { value: 'rate', label: `${crimeName} Rate` },
          { value: 'national_rate', label: `${(isCountyOrCity || isMetro) ? compLabel : 'National'} Rate` },
          { value: 'rate_diff', label: `${(isCountyOrCity || isMetro) ? compLabel : 'National'} Rate Difference` },
          { value: 'rate_diff_pct', label: `% ${(isCountyOrCity || isMetro) ? compLabel : 'National'} Rate Difference` },
          { value: 'rate_vs_national_rate', label: `${crimeName} Rate vs ${(isCountyOrCity || isMetro) ? compLabel : 'National'} Rate`, isDefault: true },
          { value: 'rank', label: `${isCountyOrCity ? compLabel : locationType} Rank` },
          { value: 'percentile', label: `${isCountyOrCity ? compLabel : locationType} Percentile` }
        ];
      }
      case 'scores':
        return [
          { value: 'composite_score', label: `${comparisonPrefix ? `${comparisonPrefix} ` : ''}KRIME Score™ (Composite)`, isDefault: true },
          { value: 'composite_score_rank', label: `${comparisonPrefix ? `${comparisonPrefix} ` : ''}${locationType} Rank (Composite)` },
          { value: 'score', label: `${comparisonPrefix ? `${comparisonPrefix} ` : ''}${selectedCrime === 'all_crime' ? 'All ' : ''}${crimeName} Score (Non-Weighted)` },
          { value: 'rank', label: `${comparisonPrefix ? `${comparisonPrefix} ` : ''}${locationType} Rank (Non-Weighted)` }
        ];
      default:
        return [];
    }
  }, [
    locationTypeData?.data?.locations,
    selectedCrime, 
    selectedSource,
    selectedLocation?.info,
    activeTab, 
    isNation,
    isCountyOrCity,
    isMetro,
    compareMode,
    rateViewMode,
    stateAbbr,
    isCounty
  ]);

  useEffect(() => {
    const options = getRangeOptions();
    const defaultOption = options.find(opt => opt.isDefault);
    if (defaultOption && !currentRange) {
      onMinMaxChange?.(defaultOption.value);
    }
  }, [activeTab, selectedCrime, selectedLocation?.info?.type, getRangeOptions, currentRange, onMinMaxChange]);

  const handleMouseEnter = () => {
    map.doubleClickZoom.disable();
  };

  const handleMouseLeave = () => {
    map.doubleClickZoom.enable();
  };

  if (!selectedLocation?.info?.name || (isNation && activeTab !== 'trends')) {
    return null;
  }

  return (
    <div className="leaflet-bottom leaflet-left map-minmax">
      <div 
        className="leaflet-control"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <button 
          className={`map-minmax-button ${showMenu ? 'active' : ''}`}
          onClick={() => setShowMenu(!showMenu)}
        >
          <ChartNoAxesCombined size={20} className="map-minmax-icon" />
        </button>
        
        {showMenu && (
          <div className="map-minmax-menu">
            {getRangeOptions().map((option, index) => {
              const isSelected = currentRange === option.value;
              
              const shouldShowEllipsis = (isCountyOrCity || (isMetro && activeTab === 'rates')) && 
                (
                  (activeTab === 'rates' && index === 1) ||

                  (!isOneCountyOrCity && !isMetro && (
                    ((activeTab === 'reports' || activeTab === 'totals') && index === 1) || 
                    (activeTab === 'trends' && index === 5) ||
                    (activeTab === 'scores' && index === 1)
                  ))
                );
              
              return (
                <div key={option.value} className="map-compare-menu-container">
                  <button
                    className={`map-minmax-item ${isSelected ? 'selected' : ''}`}
                    onClick={() => {
                      if (!isSelected) {
                        onMinMaxChange?.(option.value);
                        setShowMenu(false);
                      }
                    }}
                    disabled={isSelected}
                  >
                    {option.label}
                  </button>
                  
                  {shouldShowEllipsis && (
                    <div 
                      className="map-ellipsis-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setCompareMenuOpen(!compareMenuOpen);
                        setCompareButtonRef(e.currentTarget);
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(26, 44, 78, 0.1)';
                      }}
                      onMouseLeave={(e) => {
                        if (!compareMenuOpen) {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }
                      }}
                    >
                      <Ellipsis size={16} color="black" />
                    </div>
                  )}
                </div>
              );
            })}
            
            {compareMenuOpen && compareButtonRef && (
              <div
                ref={compareMenuRef}
                className={`map-compare-menu ${activeTab === 'trends' ? 'trends-menu' : ''}`}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
              >
                {isMetro && activeTab === 'rates' ? (
                  <>
                    <button
                      className="map-compare-option"
                      disabled={rateViewMode.rateType === 'metro'}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (rateViewMode.rateType !== 'metro') {
                          onCompareModeChange({ 
                            type: 'rates', 
                            mode: { 
                              rateType: 'metro', 
                              rateScope: 'national' 
                            } 
                          });
                        }
                        setCompareMenuOpen(false);
                      }}
                    >
                      <span>Metro</span>
                    </button>
                    <button
                      className="map-compare-option"
                      disabled={rateViewMode.rateType === 'state'}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (rateViewMode.rateType !== 'state') {
                          onCompareModeChange({ 
                            type: 'rates', 
                            mode: { 
                              rateType: 'state', 
                              rateScope: 'national' 
                            } 
                          });
                        }
                        setCompareMenuOpen(false);
                      }}
                    >
                      <span>National</span>
                    </button>
                  </>
                ) : (
                  <>
                    {!isOneCountyOrCity && (
                      <>
                        <button
                          className="map-compare-option"
                          disabled={activeTab === 'rates' 
                            ? (rateViewMode.rateType === (isCounty ? 'county' : 'city') && rateViewMode.rateScope === 'state')
                            : compareMode === 'state'}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (activeTab === 'rates') {
                              if (!(rateViewMode.rateType === (isCounty ? 'county' : 'city') && rateViewMode.rateScope === 'state')) {
                                onCompareModeChange({ 
                                  type: 'rates', 
                                  mode: { 
                                    rateType: isCounty ? 'county' : 'city', 
                                    rateScope: 'state' 
                                  } 
                                });
                                if (onUnifiedCompareModeChange) {
                                  onUnifiedCompareModeChange('state');
                                }                      
                              }
                            } else if (compareMode !== 'state') {
                              onCompareModeChange({ 
                                type: activeTab === 'reports' ? 'totals' : activeTab === 'totals' ? 'totals' : 
                                    activeTab === 'trends' ? 'trends' : 'scores', 
                                mode: 'state' 
                              });
                            }
                            setCompareMenuOpen(false);
                          }}
                        >
                          <span>
                            {stateAbbr} {isCounty ? 'County' : 'City'}
                          </span>
                        </button>
                        
                        {activeTab === 'rates' && (
                          <button
                            className="map-compare-option"
                            disabled={
                              ((isCounty ? rateViewMode.rateType === 'all_counties' : rateViewMode.rateType === 'all_cities') &&
                                rateViewMode.rateScope === 'state') ||
                              (rateViewMode.rateType === 'state' && rateViewMode.rateScope === 'state')
                            }
                            onClick={(e) => {
                              e.stopPropagation();
                              const aggregateState =
                                (isCounty ? rateViewMode.rateType === 'all_counties' : rateViewMode.rateType === 'all_cities') &&
                                rateViewMode.rateScope === 'state';
                              const legacyState = rateViewMode.rateType === 'state' && rateViewMode.rateScope === 'state';
                              if (!(aggregateState || legacyState)) {
                                onCompareModeChange({ 
                                  type: 'rates', 
                                  mode: { 
                                    rateType: isCounty ? 'all_counties' : 'all_cities', 
                                    rateScope: 'state' 
                                  } 
                                });
                                if (onUnifiedCompareModeChange) {
                                  onUnifiedCompareModeChange('state');
                                }                      
                                setCompareMenuOpen(false);
                              }
                            }}
                          >
                            <span>
                              {stateAbbr} State
                            </span>
                          </button>
                        )}
                      </>
                    )}
                    
                    <button
                      className="map-compare-option"
                      disabled={activeTab === 'rates' 
                        ? (rateViewMode.rateType === (isCounty ? 'county' : 'city') && rateViewMode.rateScope === 'national')
                        : compareMode === 'national'}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (activeTab === 'rates') {
                          if (!(rateViewMode.rateType === (isCounty ? 'county' : 'city') && rateViewMode.rateScope === 'national')) {
                            onCompareModeChange({ 
                              type: 'rates', 
                              mode: { 
                                rateType: isCounty ? 'county' : 'city', 
                                rateScope: 'national' 
                              } 
                            });
                            if (onUnifiedCompareModeChange) {
                              onUnifiedCompareModeChange('national');
                            }                   
                          }
                        } else if (compareMode !== 'national') {
                          onCompareModeChange({ 
                            type: activeTab === 'reports' ? 'totals' : activeTab === 'totals' ? 'totals' : 
                                activeTab === 'trends' ? 'trends' : 'scores', 
                            mode: 'national' 
                          });
                        }
                        setCompareMenuOpen(false);
                      }}
                    >
                      <span>
                        US {isCounty ? 'County' : 'City'}
                      </span>
                    </button>
                    
                    {activeTab === 'rates' && (
                      <button
                        className="map-compare-option"
                        disabled={
                          ((isCounty ? rateViewMode.rateType === 'all_counties' : rateViewMode.rateType === 'all_cities') &&
                            rateViewMode.rateScope === 'national') ||
                          (rateViewMode.rateType === 'national' && rateViewMode.rateScope === 'national')
                        }
                        onClick={(e) => {
                          e.stopPropagation();
                          const aggregateNat =
                            (isCounty ? rateViewMode.rateType === 'all_counties' : rateViewMode.rateType === 'all_cities') &&
                            rateViewMode.rateScope === 'national';
                          const legacyNat = rateViewMode.rateType === 'national' && rateViewMode.rateScope === 'national';
                          if (!(aggregateNat || legacyNat)) {
                            onCompareModeChange({ 
                              type: 'rates', 
                              mode: { 
                                rateType: isCounty ? 'all_counties' : 'all_cities', 
                                rateScope: 'national' 
                              } 
                            });
                            if (onUnifiedCompareModeChange) {
                              onUnifiedCompareModeChange('national');
                            }
                            setCompareMenuOpen(false);
                          }
                        }}
                      >
                        <span>
                          US National
                        </span>
                      </button>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MapMinMax;